import { api } from './client'
import { requireArray } from './readShape'

/**
 * Report packs (spec 172) — several of your scheduled reports on ONE schedule, delivered
 * as ONE email (one Excel workbook with a sheet per report, or links to each).
 *
 * cb-api answers one camelCase shape from every route (`packToApi` / `runToApi`). A pack's
 * members are COPIED server-side from the caller's own schedules, so this service sends
 * schedule ids — never filters.
 */

/** The error sentence a refused request carries, whatever shape the body arrived in. */
async function refusal(err, fallback) {
  const data = err?.response?.data
  try {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      const parsed = JSON.parse(await data.text())
      if (typeof parsed?.error === 'string' && parsed.error.trim()) return parsed.error.trim()
    } else if (typeof data?.error === 'string' && data.error.trim()) {
      return data.error.trim()
    }
  } catch { /* fall through to the fallback */ }
  return fallback
}

const inPlainWords = (fallback) => async (err) => {
  const e = new Error(await refusal(err, fallback))
  e.status = err?.response?.status ?? null
  throw e
}

const reportPackService = {
  list: async () => {
    const data = await api.get('/report-packs').catch(inPlainWords("Your report packs couldn't be loaded."))
    // An unreadable body must not render as "you have no packs" (readShape.js).
    return requireArray(data?.data?.packs, { noun: 'report pack list', emptyNoun: 'report pack list' })
  },

  get: async (id) => {
    const data = await api.get(`/report-packs/${encodeURIComponent(id)}`).catch(inPlainWords("This report pack couldn't be loaded."))
    return data?.data?.pack ?? null
  },

  create: async (payload) => {
    const data = await api.post('/report-packs', payload).catch(inPlainWords("The report pack couldn't be saved."))
    return data?.data?.pack ?? null
  },

  update: async (id, payload) => {
    const data = await api.put(`/report-packs/${encodeURIComponent(id)}`, payload).catch(inPlainWords("The report pack couldn't be saved."))
    return data?.data?.pack ?? null
  },

  remove: async (id) => api.delete(`/report-packs/${encodeURIComponent(id)}`).catch(inPlainWords("The report pack couldn't be deleted.")),

  runNow: async (id) => {
    const data = await api.post(`/report-packs/${encodeURIComponent(id)}/run`, {}).catch(inPlainWords("The report pack couldn't be started."))
    return data?.data?.run ?? null
  },

  runs: async (id) => {
    const data = await api.get(`/report-packs/${encodeURIComponent(id)}/runs`).catch(inPlainWords("This pack's runs couldn't be loaded."))
    return requireArray(data?.data?.runs, { noun: 'pack run list', emptyNoun: 'pack run list' })
  },

  /**
   * One workbook from 2–8 of the caller's own finished runs. Resolves to `{ blob, filename }`;
   * rejects with cb-api's own sentence (it names the run that could not be combined).
   */
  combine: async (jobIds, timeZone) => {
    const data = await api.post('/report-packs/combine', { jobIds, timeZone }, { responseType: 'blob', timeout: 180_000 })
      .catch(inPlainWords("The runs couldn't be combined into one workbook."))
    return { blob: data, filename: null }
  },
}

/** Save a Blob as a file — the same anchor-click every report export uses. */
export function saveBlob(blob, filename) {
  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objUrl)
}

export default reportPackService
